import { useState, useRef, useEffect } from 'react';
import { Sparkles, X, Send, Mic, MicOff, Volume2, VolumeX, RefreshCw, Zap } from 'lucide-react';
import axios from 'axios';
import { useVoice } from '../hooks/useVoice';

const API = process.env.REACT_APP_BACKEND_URL;
const CONV_ID = 'sofia-main';

const SOFIA_GREETING = {
  type: 'message',
  message: "Hi! I'm Sofia, your creative AI director 🎨 I can write content, brainstorm ideas, check your queue, run research — basically anything on the platform. What are we creating today?",
  suggestions: [
    'Write a LinkedIn post about my latest project',
    'Brainstorm content ideas for next week',
    'Check what\'s in my approval queue',
    'Help me repurpose my blog content',
  ],
};

export function SofiaAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: SOFIA_GREETING, ts: Date.now() },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [executingId, setExecutingId] = useState(null);
  const [execResults, setExecResults] = useState({});
  const [showPulse, setShowPulse] = useState(true);
  // Proactive nudge: shown after 2 min of inactivity when Sofia is closed
  const [showNudge, setShowNudge] = useState(false);
  const nudgeTimerRef = useRef(null);
  const nudgeDismissed = useRef(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  // Female voice for Sofia
  const { voiceEnabled, setVoiceEnabled, isListening, isSpeaking, speak, startListening } = useVoice({ voice: 'af_bella' });

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Stop pulsing after first open
  useEffect(() => {
    if (isOpen) {
      setShowPulse(false);
      setShowNudge(false);
      nudgeDismissed.current = true; // once opened, no more nudges this session
      if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
    }
  }, [isOpen]);

  // Start 2-minute proactive nudge timer on mount
  useEffect(() => {
    if (nudgeDismissed.current) return;
    nudgeTimerRef.current = setTimeout(() => {
      if (!nudgeDismissed.current) setShowNudge(true);
    }, 120_000); // 2 minutes
    return () => clearTimeout(nudgeTimerRef.current);
  }, []);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg, ts: Date.now() }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/strategist/chat`, {
        message: msg,
        conversation_id: CONV_ID,
        persona: 'sofia',
      });
      const resp = data.response;
      setMessages(prev => [...prev, { role: 'assistant', content: resp, ts: Date.now() }]);
      const txt = typeof resp === 'string' ? resp : resp?.message || '';
      speak(txt.slice(0, 350));
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: { type: 'message', message: "Hmm, I hit a snag! Try again in a moment." },
        ts: Date.now(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const executeAction = async (action) => {
    setExecutingId(action.id);
    try {
      const method = (action.method || 'POST').toLowerCase();
      const res = method === 'get'
        ? await axios.get(`${API}${action.endpoint}`)
        : await axios.post(`${API}${action.endpoint}`, action.body);
      setExecResults(prev => ({ ...prev, [action.id]: `Done! ${JSON.stringify(res.data).slice(0, 100)}` }));
      await send(`I just ran "${action.description}". What's next?`);
    } catch (err) {
      setExecResults(prev => ({ ...prev, [action.id]: `Oops: ${String(err).slice(0, 80)}` }));
    } finally {
      setExecutingId(null);
    }
  };

  const reset = async () => {
    try { await axios.delete(`${API}/api/strategist/reset/${CONV_ID}`); } catch {}
    setMessages([{ role: 'assistant', content: SOFIA_GREETING, ts: Date.now() }]);
    setExecResults({});
  };

  const parseContent = (content) => {
    if (typeof content === 'string') return { type: 'message', message: content };
    return content;
  };

  return (
    <>
      {/* Floating button */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
        {/* Proactive nudge bubble — appears after 2 min */}
        {showNudge && !isOpen && (
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-xl shadow-2xl text-xs font-medium animate-fade-in cursor-pointer"
            style={{
              background: 'linear-gradient(135deg,rgba(249,115,22,0.95),rgba(236,72,153,0.95))',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.15)',
              backdropFilter: 'blur(12px)',
              maxWidth: 220,
              animation: 'sofiaSlideUp 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards',
            }}
            onClick={() => { setShowNudge(false); setIsOpen(true); }}
          >
            <span style={{ fontSize: 16 }}>✨</span>
            <div>
              <div className="font-bold text-[11px]">Hey, it's Sofia!</div>
              <div style={{ opacity: 0.9, fontSize: 10 }}>Need help with anything?</div>
            </div>
            <button
              data-testid="sofia-nudge-dismiss"
              onClick={e => { e.stopPropagation(); setShowNudge(false); nudgeDismissed.current = true; }}
              className="ml-1 p-0.5 rounded hover:bg-white/20 transition-all flex-shrink-0"
            >
              <X size={11} />
            </button>
          </div>
        )}

        {/* Tooltip bubble */}
        {hovered && !isOpen && !showNudge && (
          <div
            className="px-3 py-2 rounded-xl text-xs font-medium shadow-lg"
            style={{
              background: 'linear-gradient(135deg,#f97316,#ec4899)',
              color: 'white',
              whiteSpace: 'nowrap',
              animation: 'sofiaSlideUp 0.25s ease-out forwards',
            }}
          >
            Hi! I'm Sofia — need help? ✨
          </div>
        )}

        <div className="relative">
          {/* Outer pulse ring */}
          {showPulse && !isOpen && (
            <span
              className="absolute inset-0 rounded-full animate-ping"
              style={{ background: 'rgba(249,115,22,0.35)', animationDuration: '1.8s' }}
            />
          )}
          {/* Idle ring */}
          {!showPulse && !isOpen && (
            <span
              className="absolute inset-0 rounded-full"
              style={{
                background: 'rgba(249,115,22,0.15)',
                animation: 'sofiaIdle 3s ease-in-out infinite',
              }}
            />
          )}
          <button
            data-testid="sofia-toggle-btn"
            onClick={() => setIsOpen(o => !o)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            className="relative w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-transform hover:scale-105 active:scale-95"
            style={{
              background: 'linear-gradient(135deg,#f97316,#ec4899)',
              animation: showNudge ? 'sofiaButtonGlow 1.5s ease-in-out infinite' : undefined,
            }}
            title="Sofia — AI Creative Director"
          >
            {isOpen
              ? <X size={20} className="text-white" />
              : <Sparkles size={22} className="text-white" />}
          </button>
        </div>
      </div>

      {/* Chat panel */}
      {isOpen && (
        <div
          data-testid="sofia-panel"
          className="fixed bottom-24 right-6 z-50 flex flex-col rounded-2xl shadow-2xl overflow-hidden"
          style={{
            width: 380,
            height: 520,
            background: 'rgba(12,8,20,0.97)',
            border: '1px solid rgba(249,115,22,0.25)',
            backdropFilter: 'blur(20px)',
            animation: 'sofiaOpen 0.35s cubic-bezier(0.34,1.56,0.64,1) forwards',
            transformOrigin: 'bottom right',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center gap-3 px-4 py-3 flex-shrink-0"
            style={{ background: 'linear-gradient(90deg,rgba(249,115,22,0.15),rgba(236,72,153,0.12))', borderBottom: '1px solid rgba(249,115,22,0.2)' }}
          >
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg,#f97316,#ec4899)' }}
            >
              <Sparkles size={17} className="text-white" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-bold text-white">Sofia</div>
              <div className="text-[10px]" style={{ color: 'rgba(249,115,22,0.75)' }}>AI Creative Director · Full Platform Access</div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setVoiceEnabled(v => !v)}
                data-testid="sofia-voice-btn"
                title={voiceEnabled ? 'Mute Sofia' : 'Enable Sofia voice'}
                className="w-7 h-7 rounded-lg flex items-center justify-center"
                style={{ background: voiceEnabled ? 'rgba(249,115,22,0.2)' : 'rgba(255,255,255,0.06)', border: `1px solid ${voiceEnabled ? 'rgba(249,115,22,0.4)' : 'rgba(255,255,255,0.1)'}` }}
              >
                {isSpeaking ? <Volume2 size={11} className="animate-pulse" style={{ color: '#f97316' }} />
                  : voiceEnabled ? <Volume2 size={11} style={{ color: '#f97316' }} />
                  : <VolumeX size={11} style={{ color: 'rgba(255,255,255,0.3)' }} />}
              </button>
              <button onClick={reset} data-testid="sofia-reset-btn" className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ opacity: 0.5 }}>
                <RefreshCw size={11} style={{ color: 'rgba(255,255,255,0.5)' }} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.map((msg, i) => {
              const parsed = msg.role === 'assistant' ? parseContent(msg.content) : null;
              const isUser = msg.role === 'user';

              return (
                <div key={i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] ${isUser ? '' : ''}`}>
                    {isUser ? (
                      <div className="px-3 py-2 rounded-xl text-xs text-white" style={{ background: 'rgba(249,115,22,0.2)', border: '1px solid rgba(249,115,22,0.3)' }}>
                        {msg.content}
                      </div>
                    ) : (
                      <div>
                        {parsed?.message && (
                          <div className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>
                            {parsed.message}
                          </div>
                        )}
                        {parsed?.questions?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {parsed.questions.map((q, qi) => (
                              <div key={qi} className="text-xs italic" style={{ color: 'rgba(249,115,22,0.85)' }}>{q}</div>
                            ))}
                          </div>
                        )}
                        {parsed?.actions?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {parsed.actions.map(action => (
                              <button
                                key={action.id}
                                onClick={() => executeAction(action)}
                                disabled={executingId === action.id}
                                data-testid={`sofia-action-${action.id}`}
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all disabled:opacity-50"
                                style={{ background: 'rgba(249,115,22,0.15)', border: '1px solid rgba(249,115,22,0.35)', color: '#f97316' }}
                              >
                                <Zap size={9} />
                                {executingId === action.id ? 'Working...' : action.label}
                              </button>
                            ))}
                          </div>
                        )}
                        {execResults[parsed?.actions?.[0]?.id] && (
                          <div className="mt-1.5 text-[10px]" style={{ color: 'rgba(249,115,22,0.6)' }}>
                            {execResults[parsed.actions[0].id]}
                          </div>
                        )}
                        {parsed?.suggestions?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {parsed.suggestions.slice(0, 3).map((s, si) => (
                              <button
                                key={si}
                                onClick={() => send(s)}
                                className="text-[10px] px-2 py-1 rounded-lg"
                                style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)', color: 'rgba(249,115,22,0.7)' }}
                              >
                                {s}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {loading && (
              <div className="flex gap-1.5 pl-1">
                {[0, 1, 2].map(i => (
                  <div key={i} className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: '#f97316', animationDelay: `${i * 0.15}s` }} />
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <div className="px-3 py-2.5 flex-shrink-0" style={{ borderTop: '1px solid rgba(249,115,22,0.15)' }}>
            <div className="flex gap-2 items-center">
              <input
                ref={inputRef}
                data-testid="sofia-input"
                className="flex-1 text-xs px-3 py-2 rounded-xl outline-none resize-none"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(249,115,22,0.2)', color: 'white', '::placeholder': { color: 'rgba(255,255,255,0.3)' } }}
                placeholder="Ask Sofia anything..."
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') send(); }}
              />
              <button
                onClick={() => startListening(t => { setInput(t); })}
                data-testid="sofia-mic-btn"
                className="w-8 h-8 rounded-xl flex items-center justify-center transition-all flex-shrink-0"
                style={{ background: isListening ? 'rgba(255,80,80,0.2)' : 'rgba(255,255,255,0.06)', border: `1px solid ${isListening ? 'rgba(255,80,80,0.4)' : 'rgba(249,115,22,0.2)'}` }}
              >
                {isListening ? <MicOff size={12} className="animate-pulse" style={{ color: '#ff5050' }} /> : <Mic size={12} style={{ color: 'rgba(249,115,22,0.7)' }} />}
              </button>
              <button
                onClick={() => send()}
                disabled={!input.trim() || loading}
                data-testid="sofia-send-btn"
                className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 disabled:opacity-30 transition-all"
                style={{ background: 'linear-gradient(135deg,#f97316,#ec4899)' }}
              >
                <Send size={13} className="text-white" />
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes sofiaIdle {
          0%, 100% { transform: scale(1); opacity: 0.15; }
          50% { transform: scale(1.15); opacity: 0.3; }
        }
        @keyframes sofiaOpen {
          from { opacity: 0; transform: scale(0.85) translateY(12px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes sofiaSlideUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes sofiaButtonGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(249,115,22,0); }
          50%       { box-shadow: 0 0 18px 4px rgba(249,115,22,0.35); }
        }
      `}</style>
    </>
  );
}
